import * as z from "zod";
import { code, codeName, pagination } from "../index";
import { demandRunStatuses, demandSkipReasons, poStatuses } from "./enums";

// The demand of the purchase orders (50-api/procurement.md §2.10; FR-M5-01, ADR-143): a run reads the sales orders of a
// season, and orders what no PO covers yet in POs `generated`, one per brand, supplier, company and warehouse.

/** A run for a season, and for one brand when it names it. */
export const runDemandInput = z.object({
  /** Code of the season. */
  season: code,
  brand: z.uuid().optional(),
});
export type RunDemandInput = z.input<typeof runDemandInput>;

/** The runs, newest first. */
export const listDemandRunsInput = z.object({
  /** Code of the season. */
  season: code.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListDemandRunsInput = z.input<typeof listDemandRunsInput>;

export const getDemandRunInput = z.object({ demandRunId: z.uuid() });
export type GetDemandRunInput = z.input<typeof getDemandRunInput>;

/** Demand a run left out, by reason and brand: what the next run takes, once what is missing is there. */
const demandSkipped = z.object({
  reason: z.enum(demandSkipReasons),
  brandOrgId: z.uuid(),
  /** The lines of sales orders, and their units still to cover. */
  soLineCount: z.number().int(),
  units: z.number().int(),
});

/** What a run did, kept on its row (`demand_run.result`). */
export const demandRunResult = z.object({
  purchaseOrderCount: z.number().int(),
  lineCount: z.number().int(),
  unitCount: z.number().int(),
  skipped: z.array(demandSkipped),
});
export type DemandRunResult = z.output<typeof demandRunResult>;

/** A run of the demand: in the list and as it ended. */
export const demandRunDto = z
  .object({
    id: z.uuid(),
    season: z.string(),
    brandOrgId: z.uuid().nullable(),
    brand: z.string().nullable(),
    status: z.enum(demandRunStatuses),
    /** Null for a run that did not end, which a run never leaves: it is one request. */
    result: demandRunResult.nullable(),
    /** Who asked for it, by name. */
    createdBy: z.string().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: "DemandRun" });

/** A run with the POs it created, and the names of the brands it left out. */
export const demandRunDetailDto = demandRunDto
  .extend({
    purchaseOrders: z.array(
      z.object({
        id: z.uuid(),
        poNumber: z.string(),
        status: z.enum(poStatuses),
        supplier: z.string(),
        destination: codeName,
        qtyOrdered: z.number().int(),
      }),
    ),
    /** The names of the brands `result.skipped` names: the run keeps them by id. */
    skippedBrands: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
  })
  .meta({ id: "DemandRunDetail" });

/** v0.2 §9, "PO per fornitore": the POs the Actor sees, counted by supplier, season and status. */
export const listSupplierPurchaseTotalsInput = z.object({
  /** Code of the season. */
  season: code.optional(),
  supplier: z.uuid().optional(),
  status: z.enum(poStatuses).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListSupplierPurchaseTotalsInput = z.input<typeof listSupplierPurchaseTotalsInput>;

/**
 * The POs of a supplier in a season and a status, with their units: ordered (the confirmed quantity where there is
 * one), received, and still to receive on the lines open of a PO neither cancelled nor closed. Cancelled lines are
 * out.
 */
export const supplierPurchaseTotalDto = z
  .object({
    supplierOrgId: z.uuid(),
    supplier: z.string(),
    season: z.string().nullable(),
    status: z.enum(poStatuses),
    purchaseOrderCount: z.number().int(),
    qtyOrdered: z.number().int(),
    qtyReceived: z.number().int(),
    qtyToReceive: z.number().int(),
  })
  .meta({ id: "SupplierPurchaseTotal" });
